import React, { useState } from 'react';
import { API_BASE_URL, CMS_API_TOKEN } from 'services/apiConfig';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

interface FileUploadProps {
  label: string;
  acceptedTypes: string[];
  onFileUploaded: (url: string, fileName: string) => void;
  isUploading?: boolean;
  uploadedFileName?: string;
  uploadedFileUrl?: string;
  required?: boolean;
  onUploadStart?: () => void;
  onUploadError?: (err: Error) => void;
  onFileSelected?: (file: File) => void;
  maxSizeGB?: number; // Default 0.1GB (100MB)
}

const FileUpload = ({
  label,
  acceptedTypes,
  onFileUploaded,
  isUploading,
  uploadedFileName,
  uploadedFileUrl,
  required = false,
  onUploadStart,
  onUploadError,
  onFileSelected = undefined,
  maxSizeGB = 0.1, // Default 0.1GB (100MB)
}: FileUploadProps) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file: File) => {
    console.log('🔄 FileUpload: Starting file handling for:', file.name);
    console.log('📊 File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
    });

    setError(null);
    setUploadProgress(0); // Reset progress when starting new upload

    // Validate file type
    const fileExtension = file.name.split('.').pop()!.toLowerCase();
    console.log('🔍 File extension detected:', fileExtension);
    console.log('✅ Accepted types:', acceptedTypes);

    if (!acceptedTypes.includes(fileExtension)) {
      const errorMsg = `Invalid file type. Please upload: ${acceptedTypes.join(', ')}`;
      console.error('❌ File type validation failed:', errorMsg);
      setError(errorMsg);
      return;
    }

    // Validate file size
    const maxSize = maxSizeGB * 1024 * 1024 * 1024; // Convert GB to bytes
    console.log(
      '📏 File size check:',
      `${file.size} bytes (max: ${maxSize} bytes / ${maxSizeGB}GB)`
    );

    if (file.size > maxSize) {
      const errorMsg = `File size must be less than ${maxSizeGB}GB`;
      console.error('❌ File size validation failed:', errorMsg);
      setError(errorMsg);
      return;
    }

    if (onFileSelected) {
      onFileSelected(file);
    }

    try {
      console.log('🚀 Starting file processing...');
      setUploadProgress(0); // Reset progress
      if (onUploadStart) {
        console.log('📢 Calling onUploadStart callback');
        onUploadStart();
      }

      // Check if we should compress the file
      let fileToUpload = file;
      if (shouldCompressFile(file)) {
        console.log(
          '🗜️ File qualifies for compression, starting compression...'
        );
        setIsCompressing(true);
        try {
          fileToUpload = await compressVideoFile(file);
          console.log('✅ Compression completed successfully');
        } catch (compressionError) {
          console.warn(
            '⚠️ Compression failed, uploading original file:',
            (compressionError as Error).message
          );
          fileToUpload = file; // Fallback to original file
        } finally {
          setIsCompressing(false);
        }
      } else {
        console.log('📤 File does not need compression, uploading directly');
      }

      await uploadFile(fileToUpload);
    } catch (err) {
      console.error('💥 Upload failed with error:', err);
      console.error('Error details:', {
        message: (err as Error).message,
        stack: (err as Error).stack,
        name: (err as Error).name,
      });
      setError((err as Error).message || 'Upload failed. Please try again.');
      setUploadProgress(0); // Reset progress on error
      setIsCompressing(false); // Reset compression state

      // Notify parent component of upload error
      if (onUploadError) {
        onUploadError(err as Error);
      }
    }
  };

  // Check if file should be compressed
  const shouldCompressFile = (file: File): boolean => {
    // Compression disabled - always return false
    console.log('🔍 Compression check (disabled):', {
      fileName: file.name,
      fileType: file.type,
      fileSize: `${Math.round(file.size / 1024 / 1024)}MB`,
      shouldCompress: false,
    });

    return false; // Never compress videos
  };

  // Compress video file using MediaRecorder API
  const compressVideoFile = async (file: File): Promise<File> => {
    console.log('🗜️ Starting video compression for:', file.name);
    console.log('📊 Original file stats:', {
      size: `${Math.round(file.size / 1024 / 1024)}MB`,
      type: file.type,
    });

    return new Promise((resolve, reject) => {
      // Create video element to load the file
      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;

      video.onloadedmetadata = () => {
        console.log('📹 Video metadata loaded:', {
          duration: `${Math.round(video.duration)}s`,
          dimensions: `${video.videoWidth}x${video.videoHeight}`,
          aspectRatio: (video.videoWidth / video.videoHeight).toFixed(2),
        });

        // Create canvas for video processing
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // Reduce resolution for compression (max 1280x720)
        const maxWidth = 1280;
        const maxHeight = 720;
        const aspectRatio = video.videoWidth / video.videoHeight;

        if (video.videoWidth > maxWidth || video.videoHeight > maxHeight) {
          if (aspectRatio > 1) {
            canvas.width = maxWidth;
            canvas.height = maxWidth / aspectRatio;
          } else {
            canvas.height = maxHeight;
            canvas.width = maxHeight * aspectRatio;
          }
        } else {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }

        console.log('🎨 Canvas setup:', {
          originalDimensions: `${video.videoWidth}x${video.videoHeight}`,
          compressedDimensions: `${canvas.width}x${canvas.height}`,
          reductionRatio: (
            (video.videoWidth * video.videoHeight) /
            (canvas.width * canvas.height)
          ).toFixed(2),
        });

        // Get video stream from canvas
        const stream = canvas.captureStream(25); // 25 FPS

        // Setup MediaRecorder with compression settings
        const mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'video/webm;codecs=vp9',
          videoBitsPerSecond: 1000000, // 1 Mbps for good compression
        });

        const chunks: BlobPart[] = [];

        mediaRecorder.ondataavailable = (event: BlobEvent) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const compressedBlob = new Blob(chunks, { type: 'video/webm' });
          const compressionRatio = (file.size / compressedBlob.size).toFixed(2);
          const sizeSavings = (
            (1 - compressedBlob.size / file.size) *
            100
          ).toFixed(1);

          console.log('✅ Compression completed:', {
            originalSize: `${Math.round(file.size / 1024 / 1024)}MB`,
            compressedSize: `${Math.round(compressedBlob.size / 1024 / 1024)}MB`,
            compressionRatio: `${compressionRatio}:1`,
            sizeSavings: `${sizeSavings}%`,
          });

          // Create a new File object from the compressed blob
          const compressedFile = new File(
            [compressedBlob],
            file.name.replace(/\.[^/.]+$/, '.webm'), // Change extension to .webm
            {
              type: 'video/webm',
              lastModified: Date.now(),
            }
          );

          resolve(compressedFile);
        };

        mediaRecorder.onerror = (event: Event) => {
          console.error('❌ MediaRecorder error:', (event as ErrorEvent).error);
          reject(
            new Error(
              `Compression failed: ${(event as ErrorEvent).error.message}`
            )
          );
        };

        // Start recording
        mediaRecorder.start(1000); // Collect data every 1000ms

        // Play video and draw frames to canvas
        video.currentTime = 0;

        const drawFrame = () => {
          if (!video.paused && !video.ended) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            requestAnimationFrame(drawFrame);
          } else if (video.ended) {
            console.log('🎬 Video processing completed');
            mediaRecorder.stop();
            video.src = ''; // Clean up
          }
        };

        video.onplay = () => {
          console.log('▶️ Starting video frame processing...');
          drawFrame();
        };

        // Start playing the video
        video.play().catch((err: Error) => {
          console.error('❌ Video play failed:', err);
          reject(new Error(`Video processing failed: ${err.message}`));
        });
      };

      video.onerror = err => {
        console.error('❌ Video loading failed:', err);
        reject(
          new Error(
            `Video loading failed: ${(err as ErrorEvent).message || 'Unknown error'}`
          )
        );
      };

      // Load the video file
      video.src = URL.createObjectURL(file);
    });
  };

  const uploadFile = async (file: File): Promise<void> => {
    console.log('📤 Preparing upload for file:', file.name);

    const formData = new FormData();
    formData.append('file', file);

    // Create path based on file type and timestamp
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const path = `submissions/${timestamp}_${sanitizedFileName}`;

    console.log('📁 Upload path:', path);
    console.log('🧹 Sanitized filename:', sanitizedFileName);

    formData.append('path', path);

    // Obtain reCAPTCHA token and append to FormData
    let recaptchaToken: string;
    if (import.meta.env.VITE_DISABLE_RECAPTCHA === 'true') {
      recaptchaToken = 'dev-bypass';
    } else {
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA not ready');
      }
      recaptchaToken = await executeRecaptcha('file_upload');
    }
    formData.append('recaptchaToken', recaptchaToken);

    const uploadUrl = import.meta.env.DEV
      ? '/api/blob-upload'
      : `${API_BASE_URL}/api/blob-upload`;
    console.log('🌐 Making request to:', uploadUrl);
    console.log('📋 FormData contents:', {
      file: file.name,
      path: path,
    });

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event: ProgressEvent) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100
          );
          console.log(
            `📈 Upload progress: ${percentComplete}% (${event.loaded}/${event.total} bytes)`
          );
          setUploadProgress(percentComplete);
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        console.log('📡 Response received:', {
          status: xhr.status,
          statusText: xhr.statusText,
          responseText: xhr.responseText.substring(0, 200) + '...',
        });

        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText);
            console.log('✅ Upload successful! Response:', result);
            console.log('🔗 File URL:', result.url);

            setUploadProgress(100); // Ensure we show 100% on success

            console.log('📢 Calling onFileUploaded callback with:', {
              url: result.url,
              fileName: file.name,
            });

            onFileUploaded(result.url, file.name);
            resolve(result);
          } catch (parseError) {
            console.error('❌ Failed to parse response JSON:', parseError);
            reject(new Error('Invalid response format from server'));
          }
        } else {
          let errorDetails = '';
          try {
            errorDetails = xhr.responseText;
            console.error('❌ Response error details:', errorDetails);
          } catch (e) {
            console.error('❌ Could not read error response:', e);
          }

          setUploadProgress(0); // Reset progress on HTTP error
          reject(
            new Error(
              `Upload failed: ${xhr.status} ${xhr.statusText}${errorDetails ? ` - ${errorDetails}` : ''}`
            )
          );
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        console.error('🚨 Network error occurred during upload');
        setUploadProgress(0); // Reset progress on network error
        reject(new Error('Network error occurred during upload'));
      });

      // Handle timeout
      xhr.addEventListener('timeout', () => {
        console.error('⏰ Upload timeout');
        setUploadProgress(0); // Reset progress on timeout
        reject(new Error('Upload timeout'));
      });

      // Configure and send request with proper API URL
      console.log('🎯 Upload URL:', uploadUrl);

      xhr.open('POST', uploadUrl);
      if (CMS_API_TOKEN) {
        xhr.setRequestHeader('Authorization', `Bearer ${CMS_API_TOKEN}`);
      }
      xhr.timeout = 300000; // 5 minutes timeout for large files
      xhr.send(formData);
    });
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div
        className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${
          dragActive
            ? 'border-red-smdd bg-red-50'
            : uploadedFileName
              ? 'border-green-500 bg-green-50'
              : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept={acceptedTypes.map(type => `.${type}`).join(',')}
          onChange={handleChange}
          disabled={isUploading}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />

        {isUploading || isCompressing ? (
          <div className="flex flex-col items-center space-y-3">
            <div className="w-8 h-8 border-2 border-red-smdd border-t-transparent rounded-full animate-spin"></div>
            <div className="w-full max-w-xs">
              <div className="flex justify-between items-center mb-1">
                <p className="text-sm text-gray-600 font-baijamjuree">
                  {isCompressing ? 'Processing...' : 'Uploading...'}
                </p>
                {!isCompressing && (
                  <span className="text-sm font-semibold text-red-smdd">
                    {uploadProgress}%
                  </span>
                )}
              </div>
              {!isCompressing && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-red-smdd h-2 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              )}
            </div>
          </div>
        ) : uploadedFileName ? (
          <div className="flex flex-col items-center space-y-2">
            <svg
              className="w-8 h-8 text-green-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-green-700 font-baijamjuree font-medium">
              {uploadedFileName}
            </p>
            {uploadedFileUrl && (
              <a
                href={uploadedFileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 underline mt-1 z-10 relative"
                onClick={e => e.stopPropagation()}
              >
                View Current File
              </a>
            )}
            <p className="text-xs text-gray-500 font-baijamjuree mt-1">
              Click to replace
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <div>
              <p className="text-sm text-gray-600 font-baijamjuree">
                <span className="font-semibold">Click to upload</span> or drag
                and drop
              </p>
              <p className="text-xs text-gray-500 font-baijamjuree">
                {acceptedTypes.join(', ').toUpperCase()} (Max{' '}
                {maxSizeGB >= 1 ? `${maxSizeGB}GB` : `${maxSizeGB * 1000}MB`})
              </p>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <p className="text-sm text-red-600 font-baijamjuree">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
